import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { lazy } from 'react';
import {
  IconFileInvoice,
  IconContract,
  IconCurrencyDollar,
  IconReceipt,
  IconShoppingCart,
  IconTruck,
  IconEye,
  IconSettings,
  IconBuildingWarehouse,
  IconBuilding,
  IconFileText,
  IconPercentage,
} from '@tabler/icons-react';
import { registerAccountingPartnerViewExtensions } from './extensions/AccountingPartnerViewExtension';

/**
 * Регистрация клиентской части модуля accounting.
 */
export async function setup(app: AppRuntime): Promise<void> {
  const accounting = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
    navigationOrder: 20,
  });

  registerAccountingPartnerViewExtensions();

  const groupDocuments = accounting.createNavigationGroup({
    key: 'documents',
    label: 'Документы',
    icon: IconFileInvoice,
    order: 1,
  });

  accounting.useNavigation([
    {
      /** Общий список всех документов — скрыт из меню */
      icon: IconFileInvoice,
      label: 'Все документы',
      url: '/accounting/documents',
      component: lazy(() => import('./components/Documents/DocumentsList')),
      hiddenFromMenu: true,
    },
    {
      group: groupDocuments,
      icon: IconFileText,
      label: 'Счета на оплату',
      url: '/accounting/invoice-docs',
      component: lazy(() => import('./components/InvoiceDocs/InvoiceDocsList')),
    },
    {
      group: groupDocuments,
      icon: IconCurrencyDollar,
      label: 'Оплаты',
      url: '/accounting/pay-docs',
      component: lazy(() => import('./components/PayDocs/PayDocsList')),
    },
    {
      group: groupDocuments,
      icon: IconReceipt,
      label: 'Реализации',
      url: '/accounting/sell-docs',
      component: lazy(() => import('./components/SellDocs/SellDocsList')),
    },
    {
      group: groupDocuments,
      icon: IconShoppingCart,
      label: 'Заказы',
      url: '/accounting/order-docs',
      component: lazy(() => import('./components/OrderDocs/OrderDocsList')),
    },
    {
      group: groupDocuments,
      icon: IconTruck,
      label: 'Документы поставщиков',
      url: '/accounting/supplier-docs',
      component: lazy(() => import('./components/SupplierDocs/SupplierDocsList')),
    },
    {
      icon: IconEye,
      label: 'Просмотр реализации',
      url: '/accounting/sell-docs/:id',
      component: lazy(() =>
        import('./components/SellDocs/views/SellDocViewPage').then(m => ({
          default: m.SellDocViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
    {
      icon: IconEye,
      label: 'Просмотр документа поставщика',
      url: '/accounting/supplier-docs/:id',
      component: lazy(() =>
        import('./components/SupplierDocs/views/SupplierDocumentViewPage').then(m => ({
          default: m.SupplierDocumentViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
    {
      group: groupDocuments,
      icon: IconContract,
      label: 'Договоры',
      url: '/accounting/contracts',
      component: lazy(() => import('./components/Contracts/ContractsList')),
    },
    {
      group: groupDocuments,
      icon: IconSettings,
      label: 'Настройки',
      url: '/accounting/settings',
      component: lazy(() => import('./components/Settings/AccountingSettingsPage')),
    },
    {
      icon: IconFileInvoice,
      label: 'Настройки типов документов',
      url: '/accounting/settings/doctypes',
      component: lazy(() => import('./components/Settings/DocTypeSettingsPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconBuildingWarehouse,
      label: 'Склады',
      url: '/accounting/settings/warehouses',
      component: lazy(() => import('./components/Settings/WarehousesPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconBuilding,
      label: 'Наши компании',
      url: '/accounting/settings/companies',
      component: lazy(() => import('./components/Settings/OurCompaniesPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconPercentage,
      label: 'Налоговые ставки',
      url: '/accounting/settings/taxes',
      component: lazy(() => import('./components/Settings/TaxesPage')),
      hiddenFromMenu: true,
    },
    {
      icon: IconFileText,
      label: 'Просмотр счёта',
      url: '/accounting/invoice-docs/:id',
      component: lazy(() =>
        import('./components/InvoiceDocs/views/InvoiceDocViewPage').then(m => ({
          default: m.InvoiceDocViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
    {
      icon: IconBuilding,
      label: 'Контрагенты',
      url: '/accounting/counterparties/:id',
      component: lazy(() =>
        import('./components/Counterparties/views/LegalEntityViewPage').then(m => ({
          default: m.LegalEntityViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
  ]);
}
